/**
 * EmptyState — branded empty-state pattern for list views.
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section D Empty-State Library.
 * Three pieces: "What is this?" + "Why would I use it?" + Primary CTA.
 *
 * Voice from 04B Section K: warm, plain, sixth-grade reading level, never
 * patronizing.
 *
 * Usage:
 *   <EmptyState surface="funnels" onCreate={() => navigate('/funnels/new')} />
 */

import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useHelpContent } from "../../contexts/HelpContentContext";

export interface EmptyStateProps {
  /** Surface key — looks up content.emptyStates[surface]. */
  surface: string;
  /** Optional click handler for the primary CTA. Required if ctaLabel is set. */
  onCreate?: () => void;
  /** Override CTA visibility. Default: shown if ctaLabel and onCreate both set. */
  showCta?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  surface,
  onCreate,
  showCta = true,
}) => {
  const { getEmptyState } = useHelpContent();
  const entry = getEmptyState(surface);

  if (!entry) {
    // Fallback so list pages never look broken.
    return (
      <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
        <Typography variant="h6">No items yet</Typography>
      </Box>
    );
  }

  const ctaVisible = showCta && entry.ctaLabel && onCreate;

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        px: 3,
        maxWidth: 560,
        mx: "auto",
      }}
    >
      {entry.icon && (
        <Box
          sx={{
            fontSize: "3rem",
            lineHeight: 1,
            mb: 2,
          }}
          aria-hidden="true"
        >
          {entry.icon}
        </Box>
      )}

      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 600,
          color: "text.primary",
          mb: 1,
        }}
      >
        {entry.whatIsThis}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "inherit" }}>Why use one?</strong>{" "}
        {entry.whyUse}
      </Typography>

      {ctaVisible && (
        <Button
          variant="contained"
          onClick={onCreate}
          size="large"
          sx={{
            backgroundColor: "#D4AC0D",
            color: "#1B1F23",
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 600,
            minHeight: 52,
            px: 3,
            "&:hover": { backgroundColor: "#B89309" },
          }}
        >
          {entry.ctaLabel}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
